import {
	Button,
	IconButton,
	MinusIcon,
	PlusIcon,
} from "@au-schein-de/au-ui-library";
import type { ProductNoEdibleInfo } from "@dransay/api";
import type {
	DeliveryMethodKind,
	ProductVendorListItem,
} from "@dransay/api/models";
import {
	getCartItemQuantityMaximum,
	isCartItemQuantityWithinCapacity,
} from "@dransay/api/models";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useRouteLoaderData } from "react-router";
import { useVendorSelection } from "~/contexts/vendor-selection.context";
import type { PriceComparisonDiscount } from "~/features/price-comparison/price-comparison-discounts";
import { useCurrencyPrice } from "~/hooks/use-currency-price.hook";
import {
	pushAddToCartMetric,
	pushProductQuantityChangedMetric,
} from "~/tooling/sentry";
import type { Route } from "../../../.react-router/types/app/+types/root";
import { useCart } from "../cart/cart.context";
import { getAmountInCartLabel } from "../cart/cart.utils";
import { ProductDetailsCartItemAddVendorConflictModal } from "./product-details-cart-item-add-vendor-conflict-modal.component";
import { selectDeliveryMethod } from "./select-delivery-method";

type ProductDetailsAddToCartProps = {
	product: ProductNoEdibleInfo;
	vendor?: ProductVendorListItem;
	priceComparisonDiscount?: PriceComparisonDiscount;
};

export function ProductDetailsAddToCart({
	product,
	vendor,
	priceComparisonDiscount,
}: ProductDetailsAddToCartProps) {
	const { setSelectedVendor, selectedDeliveryMethodKind } =
		useVendorSelection();

	return (
		<ProductDetailsAddToCartComponent
			product={product}
			vendor={vendor}
			priceComparisonDiscount={priceComparisonDiscount}
			setSelectedVendor={setSelectedVendor}
			selectedDeliveryMethodKind={selectedDeliveryMethodKind}
		/>
	);
}

type ProductDetailsAddToCartComponentProps = ProductDetailsAddToCartProps & {
	setSelectedVendor: (vendorId?: string) => Promise<void>;
	selectedDeliveryMethodKind?: DeliveryMethodKind;
	priceComparisonDiscount?: PriceComparisonDiscount;
};

// DO NOT USE IT DIRECTLY - Broken down to make it testable
export function ProductDetailsAddToCartComponent({
	product,
	vendor,
	setSelectedVendor,
	selectedDeliveryMethodKind,
	priceComparisonDiscount,
}: ProductDetailsAddToCartComponentProps) {
	const { t } = useTranslation();
	const { id } = product;

	const loaderData =
		useRouteLoaderData<Route.ComponentProps["loaderData"]>("root");
	const enableSentryCustomEvents =
		loaderData?.featureFlags?.enableSentryCustomEvents ?? false;

	const {
		vendor: currentVendor,
		remainingCapacity,
		remainingItemsLimit,
		addToCart,
		getIncrementStep,
		cartDisclosure,
		error,
		isLoading,
	} = useCart();

	const [isVendorConflictModalOpen, setIsVendorConflictModalOpen] =
		useState(false);

	const toggleVendorConflictModal = () => {
		setIsVendorConflictModalOpen((prev) => !prev);
	};

	const errorRef = useErrorRef(error);

	const incrementStep = getIncrementStep(product.category, product.packageSize);
	const maximumQuantity = getCartItemQuantityMaximum({
		category: product.category,
		incrementStep,
	});
	const isInitialQuantityWithinMaximum =
		maximumQuantity === null || incrementStep <= maximumQuantity;
	const isInitialQuantityWithinCapacity = isCartItemQuantityWithinCapacity({
		category: product.category,
		quantity: incrementStep,
		remainingCapacity,
	});

	const [selectedQuantity, setSelectedQuantity] = useState(incrementStep);
	// biome-ignore lint/correctness/useExhaustiveDependencies: a new product must reset quantity even when its step matches
	useEffect(() => {
		setSelectedQuantity(incrementStep);
	}, [product.id, incrementStep]);
	const { currencyPrice } = useCurrencyPrice(
		Number(vendor?.price) * selectedQuantity,
	);

	const onIncrementButtonClick = () => {
		const newQuantity = selectedQuantity + incrementStep;
		pushProductQuantityChangedMetric(
			{
				productId: id,
				productName: product.name,
				previousQuantity: selectedQuantity,
				newQuantity,
				changeType: "increment",
			},
			enableSentryCustomEvents,
		);
		setSelectedQuantity(newQuantity);
	};

	const onDecrementButtonClick = () => {
		const newQuantity = selectedQuantity - incrementStep;
		pushProductQuantityChangedMetric(
			{
				productId: id,
				productName: product.name,
				previousQuantity: selectedQuantity,
				newQuantity,
				changeType: "decrement",
			},
			enableSentryCustomEvents,
		);
		setSelectedQuantity(newQuantity);
	};

	const isVendorConflict = !!currentVendor && currentVendor.id !== vendor?.id;

	const onAddToCartButtonClick = async () => {
		if (isVendorConflict) {
			toggleVendorConflictModal();
			return;
		}

		await handleAddItemToCart();
	};

	const handleAddItemToCart = async ({
		replaceCart = false,
	}: {
		replaceCart?: boolean;
	} = {}) => {
		const deliveryMethod = selectDeliveryMethod(
			vendor?.deliveryMethods,
			selectedDeliveryMethodKind,
		);

		if (!vendor || !deliveryMethod) {
			return;
		}

		pushAddToCartMetric(
			{
				productId: id,
				productName: product.name,
				quantity: selectedQuantity,
				vendorId: vendor.id,
				vendorName: vendor.name,
				price: vendor.price,
			},
			enableSentryCustomEvents,
		);

		await setSelectedVendor(vendor.id);

		await addToCart({
			vendorId: vendor.id,
			deliveryMethodUuid: deliveryMethod.uuid,
			replaceCart,
			item: {
				id,
				quantity: selectedQuantity,
				crushed: false,
				priceComparison: {
					nonDiscountedPrice: priceComparisonDiscount?.nonDiscountedPrice ?? 0,
					discountPercentage: Math.round(
						Math.abs(priceComparisonDiscount?.discountPercentage ?? 0),
					),
				},
			},
		});

		// Read the current error value before the re-render is performed
		if (!errorRef.current) {
			cartDisclosure.open();
		}

		setSelectedQuantity(incrementStep);
	};

	const onClearCart = async () => {
		toggleVendorConflictModal();
		// Atomically replace the cart in a single request instead of
		// clear-then-add. The old two-step flow left the cart cookie transiently
		// empty between the clear and the add; a login/checkout in that window
		// restored the stale previous-pharmacy cart (wrong product after login).
		await handleAddItemToCart({ replaceCart: true });
	};

	const isIncrementWithinCapacity = isCartItemQuantityWithinCapacity({
		category: product.category,
		quantity: selectedQuantity + incrementStep,
		remainingCapacity,
	});
	const cartIsFull = !isInitialQuantityWithinCapacity || !remainingItemsLimit;
	const addToCartButtonDisabled =
		!vendor ||
		!selectedDeliveryMethodKind ||
		!isInitialQuantityWithinMaximum ||
		cartIsFull ||
		isLoading;

	const isOutOfStock = product.isOutOfStock;

	return (
		<div className="col-span-4 flex min-w-0 items-center gap-4 w-full h-24 md:h-fit fixed md:relative bottom-0 left-0 md:order-last px-4 md:px-0 bg-white z-(--z-index-cart-cta-mobile)">
			{!isOutOfStock && (
				<div className="flex shrink-0 items-center gap-3 bg-gray-100 rounded-full w-fit py-1.5 px-1.5 h-fit">
					<IconButton
						icon={<MinusIcon.Raw />}
						shape="circle"
						color="primary"
						disabled={selectedQuantity <= incrementStep}
						onClick={onDecrementButtonClick}
						data-testid="quantity-minus-button"
					/>
					<span className="text-body-bold min-w-10 text-center text-gray-700">
						{getAmountInCartLabel(
							t,
							selectedQuantity,
							product.category,
							product.kind,
						)}
					</span>
					<IconButton
						icon={<PlusIcon.Raw />}
						shape="circle"
						color="primary"
						disabled={
							!isIncrementWithinCapacity ||
							(maximumQuantity !== null &&
								selectedQuantity + incrementStep > maximumQuantity)
						}
						onClick={onIncrementButtonClick}
						data-testid="quantity-plus-button"
					/>
				</div>
			)}

			<Button
				className="min-w-0 flex-1 flex-col gap-0.5 whitespace-normal py-3 md:flex-row md:gap-2 md:py-2"
				variant={
					addToCartButtonDisabled || isOutOfStock ? "secondary" : "primary"
				}
				onClick={onAddToCartButtonClick}
				disabled={addToCartButtonDisabled || isOutOfStock}
				data-testid="add-to-cart"
			>
				{cartIsFull ? (
					t("cartIsFull")
				) : isOutOfStock ? (
					t("outOfStock")
				) : (
					<>
						<span className="text-body-emphasis">{t("addToCart")}</span>
						<span className="text-body" suppressHydrationWarning>
							{currencyPrice}
						</span>
					</>
				)}
			</Button>

			<ProductDetailsCartItemAddVendorConflictModal
				isOpen={!!isVendorConflictModalOpen}
				toggleOpen={toggleVendorConflictModal}
				onConfirm={onClearCart}
				currentVendorName={currentVendor?.name ?? ""}
			/>
		</div>
	);
}

const useErrorRef = (error: unknown) => {
	const errorRef = useRef(error);

	useEffect(() => {
		errorRef.current = error;
	}, [error]);

	return errorRef;
};
